import { DrawingMapping } from './drawing-mapping.model';
import { PressureUnitOfMeasureConverter, QueryResult } from './query-result.model';

export interface ClosureQueryResult extends QueryResult { }

export interface ClosureQueryParams {
  nominalSize?: number;
  clampRingId?: number;
  flanged?: boolean;
  maxDesignPressure?: number;
  designPressureUnit?: number;
  designTempMin?: number;
  designTempMax?: number;
  designTempUnit?: string;
  hingeOrientation?: number;
  designCodeId?: number;
  designFactorId?: number;
  flangeClassId?: number;
  classRatingId?: number;
}

export function closureQueryParmsFactory(obj: Partial<ClosureQueryParams>): ClosureQueryParams {
  return {
    nominalSize: obj.nominalSize,
    clampRingId: obj.clampRingId || -1,
    flanged: obj.flanged,
    maxDesignPressure: obj.maxDesignPressure,
    designPressureUnit: obj.designPressureUnit || 1,
    designTempMin: obj.designTempMin,
    designTempMax: obj.designTempMax,
    designTempUnit: obj.designTempUnit || 'F',
    hingeOrientation: obj.hingeOrientation || -1,
    designFactorId: obj.designFactorId,
    designCodeId: obj.designCodeId,
    flangeClassId: obj.flangeClassId,
    classRatingId: obj.classRatingId
  }
}

function convertTemps(unit: string, minTemp: number, maxTemp: number): { min: number, max: number } {
  if (unit !== 'C') return { min: minTemp, max: maxTemp };
  const convertTemp = (temp: number): number => Number(((temp * 1.8) + 32).toFixed(0));
  return { min: convertTemp(minTemp), max: convertTemp(maxTemp) };
}

function convertPressure(pressure: number, unit: number, converter: PressureUnitOfMeasureConverter) {
  switch (unit) {
    case 1:
      return pressure; // already in PSI
    case 2:
      return converter.fromBarToPSI(pressure);
    case 3:
      return converter.fromKpaToPSI(pressure);
    case 4:
      return converter.fromkgcm2ToPSI(pressure);
    default:
      return '';
  }
}

export function closureFiltertoQuery(converter: PressureUnitOfMeasureConverter, obj: ClosureQueryParams): string {
  let q = `?`;
  if (!!obj.nominalSize) q += `diameterId=${obj.nominalSize}&`;
  if (!!obj.clampRingId) q += obj.clampRingId === -1 ? '' : `clampRingId=${obj.clampRingId}&`;
  if (!!obj.flanged) q += `flanged=${obj.flanged}&`;
  if (!!obj.hingeOrientation) q += obj.hingeOrientation === -1 ? '' : `hingeOrientation=${obj.hingeOrientation}&`
  if (!!obj.designFactorId) q += `designFactorId=${obj.designFactorId}&`;
  if (!!obj.designCodeId) q += `designCodeId=${obj.designCodeId}&`;
  if (!!obj.flangeClassId && obj.flanged === true) q += `flangeClassId=${obj.flangeClassId}&`;
  if (!!obj.classRatingId && obj.flanged === false) q += `classRatingId=${obj.classRatingId}&`;

  if (!!obj.maxDesignPressure) {
    const convertedPressure = convertPressure(obj.maxDesignPressure, obj.designPressureUnit || -1, converter);
    q += `maxDesignPressure=${convertedPressure}&`;
  }

  if (!!obj.designTempMin || !!obj.designTempMax) {
    const convertedTemps = convertTemps(obj.designTempUnit || '', obj.designTempMin || -1, obj.designTempMax || -1);
    q += `minDesignTemp=${convertedTemps.min}&maxDesignTemp=${convertedTemps.max}&`
  }

  if (obj.flanged === false) {
    q += `flanged=false`
    return q;
  };

  return q.substring(0, q.length - 1);
}


// Generated by https://quicktype.io

export interface ClosureDetail {
  id: number;
  partNumber: string;
  productName: string;
  description: string;
  clampRing: string;
  designCodes: string;
  diameter: string;
  diameterID: string;
  flanged: string;
  flangeClass: string;
  hasDrawing: boolean;
  hingeOrientation: string;
  maxDesignTemp: string;
  minDesignTemp: string;
  notes: string;
  pressureRating: string;
  restricted: string;
  restrictedNotice: string;
  wallThickness: string;
  weight: string;
  designFactor: string;
  maxDesignPressure: string;
  maxDesignPressureInBar: string;
  designPressures: DesignPressure[];
  replacedParts: string[];
  sealMaterials: SealMaterial[];
  image?: string | null;
  drawings: DrawingMapping[];
  flangeClassID: number;
}

export interface DesignPressure {
  designFactor: number;
  inbar: number;
  id: number;
  closureProductId: number;
  designFactorId: number;
  maxDesignPressure: number;
  createdDate: string;
  createdBy: string;
  updatedDate: string;
  updatedBy: string;
}

// Generated by https://quicktype.io

export interface SealMaterial {
  id: number;
  diameter: string;
  sealMaterial: string;
  partNumber: string;
  note: string;
}
